// APA 7th edition and MLA 9th edition citation building. Formatting rules are
// applied to the fields the user types; nothing is fetched and nothing is sent
// anywhere. Italics are represented with a marker so the UI can render them and
// the plain-text copy can drop them.

export type SourceType = 'website' | 'book' | 'journal';
export type CitationStyle = 'apa' | 'mla';

export type Author = { given: string; family: string };

export type CitationInput = {
  type: SourceType;
  authors: Author[];
  title: string;
  /** Site name, publisher, or journal name depending on the source type. */
  container: string;
  year: string;
  url: string;
  accessed: string;
  volume: string;
  issue: string;
  pages: string;
  doi: string;
};

export const EMPTY_INPUT: CitationInput = {
  type: 'website',
  authors: [{ given: '', family: '' }],
  title: '',
  container: '',
  year: '',
  url: '',
  accessed: '',
  volume: '',
  issue: '',
  pages: '',
  doi: '',
};

/** Wraps a run of text that should be italic. The UI splits on these markers. */
const IT = '\u0001';

export function italic(text: string): string {
  return `${IT}${text}${IT}`;
}

export function splitItalics(value: string): Array<{ text: string; italic: boolean }> {
  return value
    .split(IT)
    .map((text, index) => ({ text, italic: index % 2 === 1 }))
    .filter((part) => part.text.length > 0);
}

export function toPlainText(value: string): string {
  return value.split(IT).join('');
}

function initials(given: string): string {
  return given
    .split(/[\s.]+/)
    .filter(Boolean)
    .map((part) => `${part[0].toUpperCase()}.`)
    .join(' ');
}

function cleanAuthors(authors: Author[]): Author[] {
  return authors.filter((author) => author.family.trim() || author.given.trim());
}

/** APA 7: Family, I. I., Family, I. I., & Family, I. I. Up to 20, then ellipsis. */
function apaAuthors(authors: Author[]): string {
  const list = cleanAuthors(authors);
  if (!list.length) return '';
  const formatted = list.map((author) =>
    author.given.trim()
      ? `${author.family.trim()}, ${initials(author.given.trim())}`
      : author.family.trim(),
  );
  if (formatted.length === 1) return formatted[0];
  if (formatted.length <= 20) {
    return `${formatted.slice(0, -1).join(', ')}, & ${formatted[formatted.length - 1]}`;
  }
  // 21 or more: first 19, ellipsis, final author.
  return `${formatted.slice(0, 19).join(', ')}, ... ${formatted[formatted.length - 1]}`;
}

/** MLA 9: Family, Given. Two authors joined by "and". Three or more use et al. */
function mlaAuthors(authors: Author[]): string {
  const list = cleanAuthors(authors);
  if (!list.length) return '';
  const full = (author: Author) =>
    author.given.trim() ? `${author.family.trim()}, ${author.given.trim()}` : author.family.trim();
  const reversed = (author: Author) =>
    author.given.trim() ? `${author.given.trim()} ${author.family.trim()}` : author.family.trim();
  if (list.length === 1) return full(list[0]);
  if (list.length === 2) return `${full(list[0])}, and ${reversed(list[1])}`;
  return `${full(list[0])}, et al.`;
}

function endWithPeriod(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  return /[.?!]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

function joinParts(parts: string[]): string {
  return parts.filter((part) => part && part.trim()).join(' ');
}

/** APA renders a retrieval date only when the source is expected to change. */
function apaDate(input: CitationInput): string {
  const year = input.year.trim();
  return year ? `(${year}).` : '(n.d.).';
}

export function buildApa(input: CitationInput): string {
  const authors = apaAuthors(input.authors);
  const lead = authors ? endWithPeriod(authors) : '';
  const date = apaDate(input);

  // APA 7: with no author, the title moves into the author position, so it is
  // written before the date rather than after it, keeping its own styling.
  const noAuthor = !lead && Boolean(input.title.trim());
  if (noAuthor) {
    const styledTitle =
      input.type === 'book' || (input.type === 'website' && !input.container.trim())
        ? `${italic(input.title.trim())}.`
        : endWithPeriod(input.title.trim());
    const rest = buildApa({ ...input, title: '', authors: [{ given: '', family: 'X' }] });
    // Drop the placeholder author and its trailing period from the rebuilt line.
    return joinParts([styledTitle, rest.replace(/^X\.\s*/, '')]);
  }

  if (input.type === 'journal') {
    const titlePart = endWithPeriod(input.title);
    const journal = input.container.trim() ? italic(input.container.trim()) : '';
    const volumePart = input.volume.trim()
      ? `${italic(input.volume.trim())}${input.issue.trim() ? `(${input.issue.trim()})` : ''}`
      : '';
    const pagesPart = input.pages.trim() ? `${input.pages.trim()}.` : '';
    const source = [journal, volumePart].filter(Boolean).join(', ');
    const doiPart = input.doi.trim()
      ? `https://doi.org/${input.doi.trim().replace(/^https?:\/\/doi\.org\//, '')}`
      : input.url.trim();
    return joinParts([
      lead,
      date,
      titlePart,
      source ? `${source},` : '',
      pagesPart,
      doiPart,
    ]).replace(/,\s+,/g, ',');
  }

  if (input.type === 'book') {
    const titlePart = input.title.trim() ? `${italic(input.title.trim())}.` : '';
    const publisher = input.container.trim() ? endWithPeriod(input.container.trim()) : '';
    return joinParts([lead, date, titlePart, publisher, input.url.trim()]);
  }

  // Website: the title is italic when no separate site name is given.
  const hasSite = Boolean(input.container.trim());
  const titlePart = input.title.trim()
    ? hasSite
      ? endWithPeriod(input.title.trim())
      : `${italic(input.title.trim())}.`
    : '';
  const sitePart = hasSite ? `${italic(input.container.trim())}.` : '';
  return joinParts([lead, date, titlePart, sitePart, input.url.trim()]);
}

export function buildMla(input: CitationInput): string {
  const authors = mlaAuthors(input.authors);
  const lead = authors ? endWithPeriod(authors) : '';

  if (input.type === 'journal') {
    const titlePart = input.title.trim() ? `"${endWithPeriod(input.title.trim())}"` : '';
    const journal = input.container.trim() ? `${italic(input.container.trim())},` : '';
    const vol = input.volume.trim() ? `vol. ${input.volume.trim()},` : '';
    const issue = input.issue.trim() ? `no. ${input.issue.trim()},` : '';
    const year = input.year.trim() ? `${input.year.trim()},` : '';
    const pages = input.pages.trim() ? `pp. ${input.pages.trim()}.` : '';
    const doi = input.doi.trim()
      ? `https://doi.org/${input.doi.trim().replace(/^https?:\/\/doi\.org\//, '')}.`
      : input.url.trim()
        ? `${input.url.trim()}.`
        : '';
    return joinParts([lead, titlePart, journal, vol, issue, year, pages, doi]);
  }

  if (input.type === 'book') {
    const titlePart = input.title.trim() ? `${italic(input.title.trim())}.` : '';
    const publisher = input.container.trim() ? `${input.container.trim()},` : '';
    const year = input.year.trim() ? `${input.year.trim()}.` : '';
    return joinParts([lead, titlePart, publisher, year]);
  }

  const titlePart = input.title.trim() ? `"${endWithPeriod(input.title.trim())}"` : '';
  const site = input.container.trim() ? `${italic(input.container.trim())},` : '';
  const year = input.year.trim() ? `${input.year.trim()},` : '';
  const url = input.url.trim() ? `${input.url.trim().replace(/^https?:\/\//, '')}.` : '';
  const accessed = input.accessed.trim() ? `Accessed ${input.accessed.trim()}.` : '';
  return joinParts([lead, titlePart, site, year, url, accessed]);
}

export function buildCitation(input: CitationInput, style: CitationStyle): string {
  return style === 'apa' ? buildApa(input) : buildMla(input);
}
